"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { type OrganizationsOutput, userRolesTranslations } from "@/interfaces";
import { OrganizationService } from "@/services/OrganizationService";
import { PlansService } from "@/services/PlansService";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { numberToMoney } from "@/util/moneyConversion";
import { formatCep, formatDocument, formatDddPhone } from "@/util/formatters";
import {
  Building2,
  MapPin,
  CreditCard,
  Mail,
  Phone,
  FileText,
  CheckCircle2,
  XCircle,
  Edit,
  Users,
  ArrowLeft,
  UserPlus,
  Globe,
  ExternalLink,
} from "lucide-react";
import type { PlansOutput } from "@/interfaces/plan";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";

const normalizeExternalUrl = (url?: string | null) => {
  if (!url) return "";
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return "";

  if (
    /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmedUrl) ||
    /^(mailto|tel):/i.test(trimmedUrl)
  ) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
};

const extractSocialHandle = (value?: string | null, domains: string[] = []) => {
  if (!value) return "";

  let sanitizedValue = value.trim();
  if (!sanitizedValue) return "";

  sanitizedValue = sanitizedValue
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "");

  for (const domain of domains) {
    const lowerDomain = domain.toLowerCase();
    if (sanitizedValue.toLowerCase().startsWith(lowerDomain)) {
      sanitizedValue = sanitizedValue.slice(lowerDomain.length);
      break;
    }
  }

  sanitizedValue = sanitizedValue.replace(/^\/+/g, "").replace(/\/+$/g, "");

  sanitizedValue = sanitizedValue.split(/[/?#]/)[0];

  sanitizedValue = sanitizedValue.replace(/^@/, "").trim();

  return sanitizedValue;
};

const formatHandleForDisplay = (
  value?: string | null,
  domains: string[] = []
) => {
  if (!value) return "";

  const handle = extractSocialHandle(value, domains);

  if (handle) {
    return `@${handle}`;
  }

  const trimmedValue = value.trim();

  return trimmedValue
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/+$/g, "");
};

const OrganizationDetails: React.FC = () => {
  const [organization, setOrganization] = useState<OrganizationsOutput | null>(
    null
  );
  const [currentPlan, setCurrentPlan] = useState<PlansOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    const organizationService = new OrganizationService();
    const plansService = new PlansService();

    const fetchData = async () => {
      if (!id) return;

      setLoading(true);
      try {
        const orgData = await organizationService.findById(id, {
          expand: ["users", "plan", "file"],
        });
        setOrganization(orgData);

        if (orgData.plan?.planId) {
          const planData = await plansService.findById(orgData.plan.planId, {
            expand: ["features"],
          });
          setCurrentPlan(planData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="container mx-auto p-4 text-center">
        <h2 className="text-xl font-bold text-gray-700">
          Organização não encontrada
        </h2>
        <p className="mt-2 text-gray-500">
          Não foi possível encontrar os dados desta organização.
        </p>
        <Button
          onClick={() => navigate("/organizacoes")}
          variant="default"
          className="mt-4 bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
        >
          Voltar para lista de organizações
        </Button>
      </div>
    );
  }

  const formatDate = (dateString?: string | Date) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  const InfoCard: React.FC<InfoCardProps> = ({ icon, label, value }) => (
    <div className="bg-gray-50 p-3 rounded-lg">
      <div className="flex items-center gap-2 mb-0.5">
        {icon}
        <h3 className="text-sm font-medium text-gray-500">{label}</h3>
      </div>
      <p className="text-sm font-medium truncate">{value || "N/A"}</p>
    </div>
  );

  return (
    <TooltipProvider>
      <div className="p-4 max-w-6xl mx-auto">
        {/* Header com navegação e ações */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/organizacoes")}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold">Detalhes da Organização</h1>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              onClick={() => navigate(`/organizacoes/${id}/membros`)}
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none border-[#EE7B11]/30 text-[#EE7B11] hover:bg-[#EE7B11]/10"
            >
              <Users className="w-4 h-4 mr-2" />
              Gerenciar Membros
            </Button>
            <Button
              onClick={() => navigate(`/organizacoes/${id}/editar`)}
              variant="default"
              size="sm"
              className="flex-1 sm:flex-none bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
            >
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Coluna da esquerda - Informações principais */}
          <div className="lg:col-span-2">
            <Card className="overflow-hidden border-none shadow-md">
              <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white py-3 px-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    {organization.file?.url ? (
                      <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center overflow-hidden">
                        <img
                          src={organization.file.url || "/placeholder.svg"}
                          alt={`Logo ${organization.name}`}
                          className="h-10 w-10 object-contain"
                        />
                      </div>
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                        <Building2 className="h-6 w-6" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-base">
                        {organization.name}
                      </CardTitle>
                      {organization.document && (
                        <p className="text-sm opacity-90">
                          CNPJ: {formatDocument(organization.document)}
                        </p>
                      )}
                    </div>
                  </div>
                  {currentPlan && (
                    <Badge className="bg-white text-[#EE7B11] hover:bg-gray-100 text-sm">
                      {currentPlan.name}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Tabs defaultValue="general" className="w-full">
                  <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0">
                    <TabsTrigger
                      value="general"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#EE7B11] data-[state=active]:bg-transparent data-[state=active]:text-[#EE7B11] py-3 px-4 text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-[#EE7B11]" />
                        Informações Gerais
                      </div>
                    </TabsTrigger>
                    <TabsTrigger
                      value="address"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#EE7B11] data-[state=active]:bg-transparent data-[state=active]:text-[#EE7B11] py-3 px-4 text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-[#EE7B11]" />
                        Endereço
                      </div>
                    </TabsTrigger>
                    <TabsTrigger
                      value="users"
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#EE7B11] data-[state=active]:bg-transparent data-[state=active]:text-[#EE7B11] py-3 px-4 text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-[#EE7B11]" />
                        Usuários
                      </div>
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="general" className="p-4 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <InfoCard
                        icon={<Mail className="h-4 w-4 text-[#EE7B11]" />}
                        label="E-mail"
                        value={organization.email}
                      />
                      <InfoCard
                        icon={<Phone className="h-4 w-4 text-[#EE7B11]" />}
                        label="Telefone"
                        value={
                          organization.phoneNumber
                            ? formatDddPhone(
                                organization.ddd,
                                organization.phoneNumber,
                                organization.internationalCode
                              )
                            : undefined
                        }
                      />
                      <InfoCard
                        icon={<FileText className="h-4 w-4 text-[#EE7B11]" />}
                        label="Documento"
                        value={formatDocument(organization.document)}
                      />
                    </div>

                    {/* Website e Redes Sociais */}
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Globe className="h-4 w-4 text-[#EE7B11]" />
                        Website e Redes Sociais
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-0.5">
                            <Globe className="h-4 w-4 text-[#EE7B11]" />
                            <h4 className="text-sm font-medium text-gray-500">
                              Website
                            </h4>
                          </div>
                          {organization.website?.trim() ? (
                            <a
                              href={normalizeExternalUrl(organization.website)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-[#EE7B11] hover:underline flex items-center gap-1 truncate"
                            >
                              {organization.website}
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </a>
                          ) : (
                            <p className="text-sm font-medium text-gray-400">
                              Não informado
                            </p>
                          )}
                        </div>

                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="h-4 w-4 text-pink-500">
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                              </svg>
                            </div>
                            <h4 className="text-sm font-medium text-gray-500">
                              Instagram
                            </h4>
                          </div>
                          {organization.socialLinks?.instagram?.trim() ? (
                            <a
                              href={normalizeExternalUrl(
                                organization.socialLinks.instagram
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-pink-500 hover:underline flex items-center gap-1 truncate"
                            >
                              {formatHandleForDisplay(
                                organization.socialLinks.instagram,
                                ["instagram.com"]
                              )}
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </a>
                          ) : (
                            <p className="text-sm font-medium text-gray-400">
                              Não informado
                            </p>
                          )}
                        </div>

                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="h-4 w-4 text-black">
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                              </svg>
                            </div>
                            <h4 className="text-sm font-medium text-gray-500">
                              X (Twitter)
                            </h4>
                          </div>
                          {organization.socialLinks?.x?.trim() ? (
                            <a
                              href={normalizeExternalUrl(organization.socialLinks.x)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-black hover:underline flex items-center gap-1 truncate"
                            >
                              {formatHandleForDisplay(organization.socialLinks.x, [
                                "x.com",
                                "twitter.com",
                              ])}
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </a>
                          ) : (
                            <p className="text-sm font-medium text-gray-400">
                              Não informado
                            </p>
                          )}
                        </div>

                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="h-4 w-4 text-black">
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                              </svg>
                            </div>
                            <h4 className="text-sm font-medium text-gray-500">
                              TikTok
                            </h4>
                          </div>
                          {organization.socialLinks?.tiktok?.trim() ? (
                            <a
                              href={normalizeExternalUrl(
                                organization.socialLinks.tiktok
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-black hover:underline flex items-center gap-1 truncate"
                            >
                              {formatHandleForDisplay(
                                organization.socialLinks.tiktok,
                                ["tiktok.com"]
                              )}
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </a>
                          ) : (
                            <p className="text-sm font-medium text-gray-400">
                              Não informado
                            </p>
                          )}
                        </div>

                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="h-4 w-4 text-blue-600">
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                              </svg>
                            </div>
                            <h4 className="text-sm font-medium text-gray-500">
                              Facebook
                            </h4>
                          </div>
                          {organization.socialLinks?.facebook?.trim() ? (
                            <a
                              href={normalizeExternalUrl(
                                organization.socialLinks.facebook
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-blue-600 hover:underline flex items-center gap-1 truncate"
                            >
                              {organization.socialLinks.facebook}
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </a>
                          ) : (
                            <p className="text-sm font-medium text-gray-400">
                              Não informado
                            </p>
                          )}
                        </div>

                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="h-4 w-4 text-red-500">
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                              </svg>
                            </div>
                            <h4 className="text-sm font-medium text-gray-500">
                              Google Empresas
                            </h4>
                          </div>
                          {organization.socialLinks?.googleProfile?.trim() ? (
                            <a
                              href={normalizeExternalUrl(
                                organization.socialLinks.googleProfile
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-medium text-red-500 hover:underline flex items-center gap-1 truncate"
                            >
                              Ver Perfil
                              <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            </a>
                          ) : (
                            <p className="text-sm font-medium text-gray-400">
                              Não informado
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="address" className="p-4">
                    {organization.address ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <InfoCard
                          icon={<MapPin className="h-4 w-4 text-[#EE7B11]" />}
                          label="Endereço"
                          value={`${organization.address.line1}, ${organization.address.number}`}
                        />
                        <InfoCard
                          icon={<MapPin className="h-4 w-4 text-[#EE7B11]" />}
                          label="Complemento"
                          value={organization.address.line2}
                        />
                        <InfoCard
                          icon={<MapPin className="h-4 w-4 text-[#EE7B11]" />}
                          label="Bairro"
                          value={organization.address.neighborhood}
                        />
                        <InfoCard
                          icon={<MapPin className="h-4 w-4 text-[#EE7B11]" />}
                          label="Cidade"
                          value={organization.address.city}
                        />
                        <InfoCard
                          icon={<MapPin className="h-4 w-4 text-[#EE7B11]" />}
                          label="CEP"
                          value={formatCep(organization.address.zipCode)}
                        />
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        <MapPin className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Nenhum endereço cadastrado</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="users" className="p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-sm font-semibold flex items-center gap-2">
                        <Users className="h-4 w-4 text-[#EE7B11]" />
                        Usuários da Organização
                      </h3>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="sm"
                            className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
                            onClick={() =>
                              navigate(`/organizacoes/${id}/membros`)
                            }
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            Adicionar Usuário
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Adicionar novo usuário à organização</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="overflow-hidden rounded-lg border">
                      <Table>
                        <TableHeader className="bg-gray-50">
                          <TableRow>
                            <TableHead className="font-semibold text-sm py-3">
                              Nome
                            </TableHead>
                            <TableHead className="font-semibold text-sm py-3">
                              Email
                            </TableHead>
                            <TableHead className="font-semibold text-sm py-3">
                              Função
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {organization.users.length > 0 ? (
                            organization.users.map((userObj, index) => {
                              const user = userObj.user;
                              return (
                                <TableRow
                                  key={index}
                                  className="hover:bg-gray-50"
                                >
                                  <TableCell className="font-medium text-sm py-3">
                                    {user?.name}
                                  </TableCell>
                                  <TableCell className="text-sm py-3">
                                    {user?.email}
                                  </TableCell>
                                  <TableCell className="py-3">
                                    <Badge
                                      variant="outline"
                                      className="font-normal border-[#EE7B11] text-[#EE7B11] text-sm"
                                    >
                                      {userRolesTranslations[userObj.role]}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={3}
                                className="text-center py-3 text-gray-500 text-sm"
                              >
                                Nenhum usuário encontrado
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Coluna da direita - Plano */}
          <div>
            <Card className="overflow-hidden border-none shadow-md">
              <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white py-3 px-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  <CardTitle className="text-base">Plano Atual</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {currentPlan ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-sm font-semibold">
                        {currentPlan.name}
                      </h3>
                      <Badge
                        variant={
                          organization.plan?.activePlan
                            ? "success"
                            : "destructive"
                        }
                        className={`text-sm ${
                          organization.plan?.activePlan
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-red-100 text-red-800 border-red-200"
                        }`}
                      >
                        {organization.plan?.activePlan ? "Ativo" : "Inativo"}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-50 p-3 rounded-lg text-center">
                        <p className="text-sm text-gray-500">Preço</p>
                        <p className="text-sm font-semibold text-[#EE7B11]">
                          {numberToMoney(currentPlan.price)}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg text-center">
                        <p className="text-sm text-gray-500">Vencimento</p>
                        <p className="text-sm font-semibold">
                          {formatDate(organization.plan?.endDate)}
                        </p>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold mb-2 text-gray-700 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-[#EE7B11]" />
                        Recursos Incluídos
                      </h4>
                      <div className="space-y-2">
                        {currentPlan.features?.map((feature, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center p-2 border-b text-sm"
                          >
                            <span>{feature.displayName}</span>
                            <Badge
                              variant="outline"
                              className={`text-sm ${
                                feature.value === "true"
                                  ? "bg-[#EE7B11]/10 text-[#EE7B11] border-[#EE7B11]/30"
                                  : feature.value === "false"
                                  ? "bg-red-50 text-red-700 border-red-200"
                                  : feature.value === "-"
                                  ? "bg-blue-50 text-blue-700 border-blue-200"
                                  : "bg-gray-50"
                              }`}
                            >
                              {feature.value === "true"
                                ? "Habilitado"
                                : feature.value === "false"
                                ? "Desabilitado"
                                : feature.value === "-"
                                ? "Ilimitado"
                                : feature.value}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-between text-sm text-gray-500 pt-2">
                      <div>
                        Início: {formatDate(organization.plan?.startDate)}
                      </div>
                      <div>
                        Término: {formatDate(organization.plan?.endDate)}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <XCircle className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-gray-500 text-sm">
                      Esta organização não possui um plano ativo no momento.
                    </p>
                    <Button
                      variant="default"
                      size="sm"
                      className="mt-4 bg-gradient-to-r from-[#EE7B11] to-[#f59642] hover:from-[#D96E0F] hover:to-[#e58a3d] text-white shadow-md"
                    >
                      Adicionar Plano
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

interface InfoCardProps {
  icon?: React.ReactNode;
  label: string;
  value?: string | number | null;
}

export default OrganizationDetails;
